import styled from "styled-components";
import { Link } from "react-router-dom";

/* The theme's secondary text is plain `grey` (#808080), which only reaches
   3.7:1 on the cream page background. These local values clear WCAG AA. */
const mutedText = "#595959"; // 6.6:1 on #FAF9EE
const gridLine = "#8f8e80"; // 3.1:1 - meets the non-text threshold
const cardBorder = "#8f8e80"; // 3.1:1, same weight as the grid
const selectionFill = "rgba(94, 139, 148, 0.34)";
const selectionEdge = "#2f6470"; // 6.6:1 against white day numbers

export const Section = styled.section`
  margin-bottom: 3rem;
`;

export const Layout = styled.div`
  display: flex;
  gap: 2rem;
  align-items: flex-start;

  @media (max-width: 900px) {
    flex-direction: column;
  }
`;

export const CalendarPane = styled.div`
  flex: 1 1 55%;
  min-width: 0;
  width: 100%;

  /* the results list runs long, so keep the calendar in view beside it */
  @media (min-width: 901px) {
    position: sticky;
    top: 1.5rem;
  }
`;

export const ResultsPane = styled.div`
  flex: 1 1 45%;
  min-width: 0;
  width: 100%;
`;

/* FullCalendar ships its own styles; these pull it into the site theme. */
export const CalendarFrame = styled.div`
  --fc-border-color: ${gridLine};
  --fc-page-bg-color: transparent;
  --fc-neutral-bg-color: transparent;
  --fc-today-bg-color: rgba(94, 139, 148, 0.22);

  font-family: ${({ theme }) => theme.fonts.primary};
  color: ${({ theme }) => theme.textColors.primary};

  .fc {
    background: ${({ theme }) => theme.backgroundColors.primary};
  }

  .fc .fc-toolbar-title {
    font-size: 1.25rem;
    font-family: ${({ theme }) => theme.fonts.primary};
  }

  .fc .fc-button-primary {
    background: ${({ theme }) => theme.buttonColors.primary};
    border-color: ${({ theme }) => theme.buttonColors.primary};
    text-transform: capitalize;
  }

  .fc .fc-button-primary:hover,
  .fc .fc-button-primary:not(:disabled):active {
    background: ${({ theme }) => theme.buttonColors.primaryHover};
    border-color: ${({ theme }) => theme.buttonColors.primaryHover};
  }

  .fc .fc-button-primary:disabled {
    background: #b7c8cb;
    border-color: #b7c8cb;
  }

  .fc .fc-daygrid-day-frame {
    min-height: 4.25rem;
    cursor: pointer;
  }

  .fc .fc-day-disabled .fc-daygrid-day-frame,
  .fc .fc-day-past .fc-daygrid-day-frame {
    cursor: default;
  }

  .fc .fc-daygrid-day-top {
    justify-content: center;
  }

  /* our own range highlight, so a tap-tap selection stays visible.
     A light tint can never reach 3:1 on this background on its own, so the
     range carries solid rules and the check-in/out days go solid. */
  .fc .cov-day-in-range {
    background: ${selectionFill};
    box-shadow:
      inset 0 2px 0 ${selectionEdge},
      inset 0 -2px 0 ${selectionEdge};
  }

  .fc .cov-day-edge {
    background: ${selectionEdge};
  }

  .fc .cov-day-edge .fc-daygrid-day-frame {
    color: #fff;
  }

  /* the count pill sits on the dark edge fill, so give it its own ground */
  .fc .cov-day-edge .cov-day-count {
    background: #fff;
    color: #1b4443;
  }

  .fc .fc-day-past .fc-daygrid-day-number {
    color: #6f6e63;
  }
`;

export const DayCell = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  padding-top: 4px;
`;

export const DayNumber = styled.span`
  font-size: 0.95rem;
`;

export const DayCount = styled.span<{ $level: "none" | "some" | "all" }>`
  font-size: 0.65rem;
  line-height: 1;
  padding: 2px 5px;
  border-radius: 10px;
  white-space: nowrap;
  font-weight: 500;
  color: ${({ $level }) => ($level === "none" ? "#7a2f2f" : "#1b4443")};
  background: ${({ $level }) =>
    $level === "none"
      ? "rgba(190, 90, 90, 0.26)"
      : $level === "some"
        ? "rgba(214, 168, 74, 0.38)"
        : "rgba(94, 139, 148, 0.3)"};
`;

export const SelectionBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
`;

export const SelectionText = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.fonts.primary};
  font-size: 1rem;
  color: ${({ theme }) => theme.textColors.primary};
`;

export const Hint = styled.p`
  margin: 0.25rem 0 1rem 0;
  font-family: ${({ theme }) => theme.fonts.primary};
  font-size: 0.9rem;
  color: ${mutedText};
`;

export const ClearButton = styled.button`
  font-family: ${({ theme }) => theme.fonts.primary};
  font-size: 0.85rem;
  padding: 0.35rem 0.9rem;
  border-radius: 999px;
  border: 1px solid ${({ theme }) => theme.buttonColors.primary};
  background: transparent;
  color: ${({ theme }) => theme.buttonColors.secondary};
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.buttonColors.primary};
    color: #fff;
  }
`;

export const ResultList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

export const ResultCard = styled.li<{ $unavailable?: boolean }>`
  display: flex;
  gap: 1rem;
  padding: 0.75rem;
  border: 1px solid ${cardBorder};
  border-radius: 8px;
  background: ${({ theme }) => theme.backgroundColors.primary};
  opacity: ${({ $unavailable }) => ($unavailable ? 0.55 : 1)};

  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

export const Thumbnail = styled.img`
  width: 140px;
  height: 100px;
  object-fit: cover;
  border-radius: 6px;
  flex-shrink: 0;

  @media (max-width: 480px) {
    width: 100%;
    height: 160px;
  }
`;

export const CardBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  min-width: 0;
`;

export const CardTitle = styled.h3`
  margin: 0;
  font-family: ${({ theme }) => theme.fonts.primary};
  font-size: 1rem;
  font-weight: 500;
  color: ${({ theme }) => theme.textColors.primary};
`;

export const CardMeta = styled.span`
  font-family: ${({ theme }) => theme.fonts.primary};
  font-size: 0.85rem;
  color: ${mutedText};
`;

export const CardLink = styled(Link)`
  align-self: flex-start;
  font-family: ${({ theme }) => theme.fonts.primary};
  font-size: 0.85rem;
  color: ${({ theme }) => theme.buttonColors.secondary};
  text-decoration: underline;
  cursor: pointer;
`;

export const Notice = styled.p`
  font-family: ${({ theme }) => theme.fonts.primary};
  font-size: 0.85rem;
  color: ${mutedText};
  margin: 0.75rem 0 0 0;
`;

export const Legend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-top: 0.75rem;
  font-family: ${({ theme }) => theme.fonts.primary};
  font-size: 0.8rem;
  color: ${mutedText};
`;

export const LegendItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
`;

export const Swatch = styled.span<{ $color: string }>`
  width: 12px;
  height: 12px;
  border-radius: 3px;
  background: ${({ $color }) => $color};
  /* the tinted swatches are too pale to find without an outline */
  border: 1px solid rgba(0, 0, 0, 0.35);
  display: inline-block;
`;
